import React from 'react';

/**
 * This helper is clear and easy to use for rendering multi-line text in React.
 *
 * Notes:
 * - React can also render arrays containing strings and elements, so the return type could be relaxed
 *   to React.ReactNode (or ReactNode[]) instead of React.ReactElement[].
 * - text.split('\n') won’t preserve Windows newlines (\r\n). If that matters for your inputs,
 *   consider normalizing first (e.g., text.replace(/\r\n/g, '\n')).
 * - Using the array index as a key is fine here since the list order/content is derived directly
 *   from the input string and not re-ordered.
 */
export function convertNewLinesToBrs(text: string): React.ReactElement[] {
  return text.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      <br />
    </React.Fragment>
  ));
}

/**
 * Converts simple HTML-like <b>...</b> tags inside a string into React elements with
 * className="font-semibold".
 *
 * - Supports literal <b>/<bold> and </b>/</bold> tags (no nesting / no attributes).
 * - Returns an array of React nodes so it can be rendered directly in JSX.
 */
export function convertBoldTagsToStrong(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const regex = /<(?:b|bold)>([\s\S]*?)<\/(?:b|bold)>/g;

  const normalizeBoundarySpaces = (raw: string) =>
    raw.replace(/^ /, '\u00A0').replace(/ $/, '\u00A0');

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const fullMatch = match[0];
    const boldText = match[1] ?? '';
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      const raw = text.slice(lastIndex, matchIndex);
      const normalized = normalizeBoundarySpaces(raw);
      nodes.push(normalized);
    }

    nodes.push(
      <span key={`b-${matchIndex}`} className="font-semibold">
        {boldText}
      </span>
    );
    lastIndex = matchIndex + fullMatch.length;
  }

  if (lastIndex < text.length) {
    const raw = text.slice(lastIndex);
    const normalized = normalizeBoundarySpaces(raw);
    nodes.push(normalized);
  }

  return nodes;
}

type ConvertTaggedTextToNodesOptions = {
  renderLink: (text: string, key: string) => React.ReactNode;
  renderBold?: (text: string, key: string) => React.ReactNode;
};

export function convertTaggedTextToNodes(
  text: string,
  { renderLink, renderBold }: ConvertTaggedTextToNodesOptions
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const regex = /<(bold|link|secondary)>([\s\S]*?)<\/\1>?/g;

  const normalizeBoundarySpaces = (raw: string) =>
    raw.replace(/^ /, '\u00A0').replace(/ $/, '\u00A0');

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const fullMatch = match[0];
    const tagName = match[1];
    const tagText = match[2] ?? '';
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      const raw = text.slice(lastIndex, matchIndex);
      const normalized = normalizeBoundarySpaces(raw);
      nodes.push(normalized);
    }

    if (tagName === 'bold') {
      nodes.push(
        renderBold ? (
          renderBold(tagText, `bold-${matchIndex}`)
        ) : (
          <span key={`bold-${matchIndex}`} className="font-semibold">
            {tagText}
          </span>
        )
      );
    }

    if (tagName === 'link') {
      nodes.push(renderLink(tagText, `link-${matchIndex}`));
    }

    if (tagName === 'secondary') {
      nodes.push(
        <span key={`secondary-${matchIndex}`} className="text-secondary">
          {tagText}
        </span>
      );
    }

    lastIndex = matchIndex + fullMatch.length;
  }

  if (lastIndex < text.length) {
    const raw = text.slice(lastIndex);
    const normalized = normalizeBoundarySpaces(raw);
    nodes.push(normalized);
  }

  return nodes;
}
